import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store/store';
import { getBalloonResidualValuesThunk } from 'actions/vehicleActions';
import { getLoanVariablesDialogState } from 'store/selectors/loanVariablesDialog.selector';
import { IDealSourceData } from 'modules/core/types/dealSource';
import { IOption } from 'modules/core/types/select';
import { IResidualValues } from 'modules/core/types/balloon';
import { SetFieldValue } from 'modules/core/types/form';
import { thousandFormatter } from 'modules/core/utils';

import { LOAN_VARIABLES_FIELDS_NAMES } from '../../consts/fieldNames.const';
import { DEFAULT_ANNUAL_MILEAGE_VALUE } from '../BalloonPayment.const';
import { getAnnualMileages, getResidualRequestParams } from '../BalloonPayment.service';
import { extractDealSourceParams } from '../utils/dealSourceData.utils';

export interface IBalloonResidualValuesResult {
  residualValues: IResidualValues | null;
  /** The annual mileage now in effect on the form, defaulted here when it had none. */
  annualMileage?: IOption;
}

/**
 * Owns the balloon residual values request.
 *
 * Fetches them as soon as a balloon vehicle is selected, independently of the form's `handleSubmit`
 * validation gate. Without this, the required (and initially empty) Annual Mileage dropdown keeps
 * the form invalid, which blocks the recalculate flow that would otherwise fetch residuals - a
 * deadlock where the dropdown can never be populated.
 *
 * The recalculate flow needs the same values and is triggered by the same change (balloon turning
 * on), so both go through `requestBalloonResidualValues`, which fetches a given set of params once
 * and shares the result.
 */
export const useRequestBalloonResidualValues = (
  balloonToggle: boolean,
  modalId: string,
  mileage: number,
  engine: IOption | undefined,
  annualMileage: IOption | undefined,
  setValue: SetFieldValue,
  overrideFormHistoryValues: (values: Record<string, unknown>) => void,
  dealSourceData?: IDealSourceData,
  selectedLenderRouteOneId?: string | null,
) => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedBalloonVehicle, balloonResidualValues } = useSelector(getLoanVariablesDialogState);
  const requestedKeyRef = useRef<string | null>(null);
  const pendingRequestRef = useRef<{ key: string; request: Promise<IResidualValues | null> } | null>(null);
  const loadedRequestRef = useRef<{ key: string; residualValues: IResidualValues | null } | null>(null);
  const [isFetchingBalloonResidualValues, setIsFetchingBalloonResidualValues] = useState(false);
  const [residualFetchFailed, setResidualFetchFailed] = useState(false);

  /*
   * A request can be started from one render and then reused by a caller that still holds an older
   * render's closure, so the values it needs are read from a ref at call time instead of captured.
   */
  const latestValuesRef = useRef({
    annualMileage,
    dealSourceData,
    selectedLenderRouteOneId,
    setValue,
    overrideFormHistoryValues,
  });

  latestValuesRef.current = {
    annualMileage,
    dealSourceData,
    selectedLenderRouteOneId,
    setValue,
    overrideFormHistoryValues,
  };

  const resolveAnnualMileage = useCallback((residualValues: IResidualValues | null) => {
    const latestValues = latestValuesRef.current;

    if (latestValues.annualMileage?.value) return latestValues.annualMileage;

    if (!getAnnualMileages(residualValues).includes(DEFAULT_ANNUAL_MILEAGE_VALUE)) return undefined;

    const annualMileageValue = {
      title: thousandFormatter(DEFAULT_ANNUAL_MILEAGE_VALUE),
      value: DEFAULT_ANNUAL_MILEAGE_VALUE,
    };

    latestValues.setValue(LOAN_VARIABLES_FIELDS_NAMES.ANNUAL_MILEAGE, annualMileageValue);
    latestValues.overrideFormHistoryValues({ annualMileage: annualMileageValue });

    return annualMileageValue;
  }, []);

  const fetchResidualValues = useCallback(
    async (requestKey: string, residualRequestParams: Record<string, any>): Promise<IResidualValues | null> => {
      setIsFetchingBalloonResidualValues(true);
      setResidualFetchFailed(false);

      try {
        const response = await dispatch(
          getBalloonResidualValuesThunk({
            params: {
              ...residualRequestParams,
              ...extractDealSourceParams(
                latestValuesRef.current.dealSourceData,
                latestValuesRef.current.selectedLenderRouteOneId,
              ),
            },
            modalId,
          }),
        );

        if (!getBalloonResidualValuesThunk.fulfilled.match(response)) {
          setResidualFetchFailed(true);

          return null;
        }

        const residualValues = (response.payload as IResidualValues | null | undefined) ?? null;

        if (getAnnualMileages(residualValues).length === 0) setResidualFetchFailed(true);

        loadedRequestRef.current = { key: requestKey, residualValues };

        return residualValues;
      } finally {
        setIsFetchingBalloonResidualValues(false);
      }
    },
    [dispatch, modalId],
  );

  const requestBalloonResidualValues = useCallback(
    async (residualRequestParams: Record<string, any>): Promise<IBalloonResidualValuesResult> => {
      const requestKey = JSON.stringify(residualRequestParams);
      const loadedRequest = loadedRequestRef.current;
      const pendingRequest = pendingRequestRef.current;
      let residualValues: IResidualValues | null;

      if (loadedRequest?.key === requestKey) {
        residualValues = loadedRequest.residualValues;
      } else if (pendingRequest?.key === requestKey) {
        residualValues = await pendingRequest.request;
      } else {
        const request = fetchResidualValues(requestKey, residualRequestParams);
        const clearPendingRequest = () => {
          if (pendingRequestRef.current?.request === request) pendingRequestRef.current = null;
        };

        pendingRequestRef.current = { key: requestKey, request };
        request.then(clearPendingRequest, clearPendingRequest);

        residualValues = await request;
      }

      return { residualValues, annualMileage: resolveAnnualMileage(residualValues) };
    },
    [fetchResidualValues, resolveAnnualMileage],
  );

  // The balloon state is reset when the vehicle changes, which invalidates what has been loaded.
  useEffect(() => {
    if (!balloonResidualValues) loadedRequestRef.current = null;
  }, [balloonResidualValues]);

  useEffect(() => {
    if (!balloonToggle || !selectedBalloonVehicle) {
      requestedKeyRef.current = null;
      setResidualFetchFailed(false);

      return;
    }

    if (getAnnualMileages(balloonResidualValues).length > 0) {
      setResidualFetchFailed(false);

      return;
    }

    const residualRequestParams = getResidualRequestParams(
      selectedBalloonVehicle,
      mileage,
      engine ?? ({ title: '', value: '' } as IOption),
    );

    if (!residualRequestParams) return;

    // Guard against re-dispatching for identical params (e.g. when the backend returns no residuals
    // for this vehicle).
    const requestKey = JSON.stringify(residualRequestParams);

    if (requestedKeyRef.current === requestKey) return;

    requestedKeyRef.current = requestKey;
    requestBalloonResidualValues(residualRequestParams);
  }, [balloonToggle, selectedBalloonVehicle, balloonResidualValues, mileage, engine, requestBalloonResidualValues]);

  return { isFetchingBalloonResidualValues, residualFetchFailed, requestBalloonResidualValues };
};
